import { API_BASE } from '../config'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * เรียก backend — โยน ApiError ที่มี status ติดมาด้วย
 * credentials: 'include' เผื่อใช้ cookie session ภายหลัง (ต้องมี origin นี้ใน CORS_ORIGINS ของ BE)
 */
/**
 * IIS บน Plesk บล็อก DELETE/PUT/PATCH เหนือชั้น node (ตอบ 405 WebDAV ไม่มี CORS header
 * → เบราว์เซอร์เห็นเป็น "Failed to fetch") ส่วน GET/POST ยอมผ่าน · จึงส่ง verb พวกนี้
 * เป็น POST แล้วแนบ X-HTTP-Method-Override ให้ backend แปลงกลับก่อน router (ดู app.js)
 * แก้ที่นี่ที่เดียวครอบคลุมทุกหน้า — ไม่ต้องแตะ web.config (เคยลองแล้ว prod ล่ม 500)
 */
const OVERRIDE = new Set(['DELETE', 'PUT', 'PATCH'])

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  if (!API_BASE) throw new ApiError('ยังไม่ได้ตั้ง VITE_API_BASE ใน .env', 0)

  const method = (init?.method ?? 'GET').toUpperCase()
  const override = OVERRIDE.has(method)

  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(override ? { 'X-HTTP-Method-Override': method } : null),
      ...init?.headers,
    },
    ...init,
    ...(override ? { method: 'POST' } : null),
  })

  if (!res.ok) {
    let detail = res.statusText
    try {
      const body = (await res.json()) as { error?: string }
      if (body.error) detail = body.error
    } catch {
      // ตอบกลับไม่ใช่ json — ใช้ statusText ไป
    }
    throw new ApiError(detail, res.status)
  }

  return (await res.json()) as T
}

/**
 * เปิดไฟล์จาก backend ในแท็บใหม่ — โหลดผ่าน fetch (ส่ง cookie) แล้วเปิดเป็น blob
 *
 * ⚠️ ทำไมไม่ใช้ <a href> ตรง ๆ: FE อยู่ simat2026 ส่วนไฟล์อยู่ apiservice (คนละ origin)
 *    ลิงก์ path ลอย ๆ จะยิงไป simat2026/api/... ที่ไม่มี และต่อให้เติม API_BASE
 *    การเปิดแท็บใหม่ข้าม origin ก็ลุ้น cookie SameSite ว่าจะติดไปไหม
 *    ดึงผ่าน call() ที่ส่ง credentials อยู่แล้วชัวร์กว่า แล้วเปิด blob ในเครื่อง
 */
export async function openFile(path: string): Promise<void> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include' })
  if (!res.ok) {
    let detail = res.statusText
    try {
      const body = (await res.json()) as { error?: string }
      if (body.error) detail = body.error
    } catch {
      // ไม่ใช่ json
    }
    throw new ApiError(detail, res.status)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank', 'noopener')
  // ปล่อย URL ทีหลังเพื่อให้แท็บใหม่โหลดทัน (เบราว์เซอร์ถือ ref ไว้จนเปิดเสร็จ)
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

/**
 * ดึงรูปจาก backend แล้วคืน blob URL (ส่ง cookie ด้วย) — ผู้เรียกต้อง revoke เองเมื่อเลิกใช้
 *
 * ⚠️ ทำไมไม่ใช้ <img src={API_BASE + path}> ตรง ๆ: FE อยู่ simat2026 รูปอยู่ apiservice
 *    (คนละ origin) · <img> ข้าม origin ไม่ส่ง cookie session (third-party cookie ถูกบล็อก)
 *    → endpoint ตอบ 401 รูปแตก · fetch credentials:'include' ส่ง cookie ได้แล้วทำเป็น blob
 *    (เสีย HTTP cache ของรูปไป แต่ cache ใน memory เองตลอดอายุหน้า — ดู SlideShow)
 */
export async function fetchBlobUrl(path: string): Promise<string> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include' })
  if (!res.ok) throw new ApiError(res.statusText, res.status)
  return URL.createObjectURL(await res.blob())
}
